import { Injectable } from "@angular/core";
import { ActionSheetController, ModalController, Platform, PopoverController } from "@ionic/angular";
import {
  ActionMenuItem,
  PopoverActionMenuComponent,
} from "src/components/popup/popover-action-menu/popover-action-menu.component";
import { PopupEnvironmentCategoryComponent } from "src/components/popup/popup-environment-category/popup-environment-category.component";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { EnumStringConverter } from "src/components/shared-components/enum/enum-string-converter";
import {
  CardEffectiveState,
  CardKind,
  CardState,
} from "src/components/shared-components/enum/enumCard";
import { CustomerStaffType } from "src/components/shared-components/enum/enumCustomerStaff";
import { EnvironmentCategory } from "src/components/shared-components/enum/enumEnvironment";
import { EquoboxMedium } from "src/components/shared-components/enum/enumEquobox";
import {
  ApplicationTheme,
  ManufacturerSystem,
} from "src/components/shared-components/enum/enumMisc";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { Card } from "src/model/class/general classes/card";
import { CustomerStaff } from "src/model/class/general classes/customer-staff";
import { Settings } from "src/model/class/settings";
import {
  ActionDeviceType,
  ActionEnvironments,
  ActionGatewayType,
  ActionInsertBillQuote,
  ActionInsertCustomer,
  ActionInsertPlant,
  ActionListGateway,
  ActionOnCard,
  ActionSinapsiDevice,
  ActionUploadFile,
} from "src/model/enum/enumActionSheetOperation";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import { BIGService } from "./big.service";
import { CardProgrammerService } from "./card-programmer.service";
import { CardsService } from "./cards.service";
import { ThemeService } from "./theme.service";
import { LoaderService } from "./loader.service";

/**
 * Handle generic action sheet
 */
@Injectable({
  providedIn: "root",
})
export class ActionSheetService implements iTranslation {
  private _translateSubscription: Subscription = null;

  private _isMobileDevice: boolean = false;
  private _isMyTODevice: boolean = false;
  private _isQ5Model: boolean = false;
  private _isQ896SModel: boolean = false;

  private _allowCardEdit: boolean = false;
  private _allowCardEnvironmentEdit: boolean = false;
  private _allowCardGenericOperation: boolean = false;
  private _allowCardDelete: boolean = false;

  //#region Translated string
  private _lblInsertModePlant: string = "";
  private _lblUploadQrCode: string = "";
  private _lblScannerQrCode: string = "";
  private _lblClose: string;
  private _lblSelectPlate: string;
  private _lblInsertManualPlant: string = "";
  private _lblTakeAPicture: string = "";
  private _lblSelectFromGallery: string = "";
  private _lblDirectConnect: string = "";
  private _lblSelectCardOperation: string = "";
  private _lblDelete: string = "";
  private _lblActivateUnlock: string = "";
  private _lblLock: string = "";
  private _lblNotify: string = "";
  private _lblWriteWithDevice: string = "";
  private _lblRealCard: string = "";
  private _lblQrCode: string = "";
  private _lblNumericCode: string = "";
  private _lblMail: string = "";
  private _lblTelephoneNumber: string = "";
  private _lblPlate: string = "";
  private _lblFaceRecognize: string = "";
  private _lblSelectCardType: string = "";
  private _lblSelectUploadstrategy: string = "";
  private _lblCardDetail: string = "";
  private _lblClone: string = "";
  private _lblNotifyToCustomer: string = "";
  private _lblNotifyToStaff: string = "";
  private _lblSetUID: string = "";
  private _lblCustomer: string = "";
  private _lblSelectNewDocument: string = "";
  private _lblBill: string = "";
  private _lblQuote: string = "";
  private _lblStaff: string = "";
  private _lblGroup: string = "";
  private _lblInsertNew: string = "";
  private _lblSetPlate1: string = "";
  private _lblSetPlate2: string = "";
  private _lblSetPlate3: string = "";
  private _lblSetPlate4: string = "";

  private _lblDescriptionWriteWithDevice: string = "";
  private _lblDescriptionCardDetail: string = "";
  private _lblDescriptionNotifyCard: string = "";
  private _lblDescriptionClone: string = "";
  private _lblDescriptionLock: string = "";
  private _lblDescriptionDelete: string = "";
  private _lblDescriptionSetUID: string = "";
  private _lblDescriptionActivateUnlock: string = "";
  private _lblGatewayActionTitle: string = "";
  private _lblViewAndModify: string = "";
  private _lblLoadConfigurationFile: string = "";
  private _lblSelectDeviceType: string = "";
  private _lblManage: string = "";
  private _lblDeviceManagement: string = "";
  private _lblAssociateExistingDevice: string = "";
  private _lblRemoveDeviceFromEnvironment: string = "";
  private _lblEdit: string = "";
  private _viewConfiguration: string = "";
  private _loadExcel: string = "";
  private _lblAddRoom: string = "";
  private _lblAccessManagement: string = "";
  private _lblAddPart: string = "";
  private _lblStartDiscovery: string = "";
  private _lblImportReadings: string = "";

  //#endregion

  private _selectServerSettingsUserSub: Subscription = null;

  constructor(
    private _actionSheetController: ActionSheetController,
    private _popoverController: PopoverController,
    private _modalController: ModalController,
    private _platform: Platform,
    private _translateService: TranslateService,
    private _bigService: BIGService,
    private _cardService: CardsService,
    private _cardProgrammerService: CardProgrammerService,
    private _loaderService: LoaderService,
    private _store: Store
  ) {
    this.checkMobileCondition();

    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.loadSettings();
  }

  /**
   * Handle showing action sheet insert plant mode
   *
   * @returns Result operation
   */
  public async openActionSheetPlantInsertMode(): Promise<any> {
    let actionSheet: any = null;

    const actionSheetConfiguration = {
      header: this._lblInsertModePlant,
      buttons: [
        {
          text: this._lblUploadQrCode,
          icon: "qr-code-outline",
          handler: () => {
            const fileChooserElement = document.getElementById(
              "qr-code-image-uploader"
            );

            if (fileChooserElement) {
              fileChooserElement.click();
            }

            actionSheet.dismiss(ActionInsertPlant.UPLOAD_EXISTING_PLANT);
          },
        },
        {
          text: this._lblInsertManualPlant,
          icon: "push-outline",
          handler: () => {
            actionSheet.dismiss(ActionInsertPlant.CREATE_NEW_PLANT);
          },
        },
        {
          text: this._lblDirectConnect,
          icon: "repeat-outline",
          handler: () => {
            actionSheet.dismiss(
              ActionInsertPlant.DIRECT_SYNCHRONIZATION_FROM_THE_SERVER
            );
          },
        },
        {
          text: this._lblClose,
          icon: "close",
          role: "cancel",
        },
      ],
    };

    if (this._isMyTODevice) {
      actionSheetConfiguration.buttons.shift();
    }

    if (this._isMobileDevice && !this._isQ5Model) {
      const objectScannerizedQrCode: any = {
        text: this._lblScannerQrCode,
        icon: "camera-outline",
        handler: () => {
          actionSheet.dismiss(ActionInsertPlant.START_SCAN);
        },
      };

      actionSheetConfiguration.buttons.unshift(objectScannerizedQrCode);
    }

    actionSheet = await this._actionSheetController.create(
      actionSheetConfiguration
    );

    actionSheet.present();

    return await actionSheet.onDidDismiss();
  }

  /**
   *  Handle showing action sheet insert plant mode
   *
   * @param selectedCard Selected card
   * @param showCardEditSetting Enable show card settings edit
   * @returns
   */
  public async openActionSheetCardOperations(
    selectedCard: Card,
    showCardEditSetting: boolean = true,
    customerStaff?: CustomerStaff
  ): Promise<any> {
    let actionSheet: any = null;
    let showClone: boolean = false;
    let showNotifyCustomer = false;
    let showNotifyStaff = false;
    let showWriteCard = false;

    let effectiveState: CardEffectiveState =
      this._cardService.calculateCardEffectiveState(
        selectedCard.active,
        selectedCard.busWritten,
        selectedCard.state,
        selectedCard.endDay,
        selectedCard.endTime
      );

    if (
      selectedCard.cardKind == CardKind.REAL_CARD ||
      selectedCard.cardKind == CardKind.GROUP_CARD ||
      selectedCard.cardKind == CardKind.QRCODE ||
      selectedCard.cardKind == CardKind.VIRTUAL_KEYPAD_CODE
    ) {
      if (selectedCard.manufacturerSystem !== ManufacturerSystem.SALTO_KS) {
        showClone = true;
      }
    }

    if (
      customerStaff !== undefined &&
      customerStaff != null &&
      effectiveState !== CardEffectiveState.EXPIRED
    ) {
      if (
        (selectedCard.cardKind == CardKind.REAL_CARD ||
          selectedCard.cardKind == CardKind.QRCODE ||
          selectedCard.cardKind == CardKind.VIRTUAL_KEYPAD_CODE) &&
        customerStaff.kind == CustomerStaffType.CUSTOMER
      ) {
        showNotifyCustomer = true;
      }
      if (
        (selectedCard.cardKind == CardKind.REAL_CARD ||
          selectedCard.cardKind == CardKind.QRCODE ||
          selectedCard.cardKind == CardKind.VIRTUAL_KEYPAD_CODE) &&
        customerStaff.kind == CustomerStaffType.STAFF
      ) {
        showNotifyStaff = true;
      }
    }

    if (
      selectedCard.cardKind === CardKind.REAL_CARD &&
      this._cardProgrammerService.cardNeedToBeWrite(
        selectedCard.manufacturerSystem
      )
    ) {
      showWriteCard = true;
    }

    //#region Build list of buttons displayed inside the action-sheet.
    //ATTENTION: The list shall be build from bottom to the top. IE:Button "Delete Card"
    //(the first inserted in the list)shall be displayed on the bottom of the sheet.
    const actionSheetConfiguration = {
      header: this._lblSelectCardOperation,
      /*
            buttons: [
              {
                text: this._lblClose,
                icon: "close",
                role: "cancel",
                handler: async () => { },
              },
            ],
      */
      buttons: [],
    };

    /*  Insert "Delete" button*/
    let arFields: ActionOnCard[] = [];
    if (this._allowCardDelete) {
      actionSheetConfiguration.buttons.unshift({
        //icon: "trash-outline",
        role: "",
        handler: () => actionSheet.dismiss(ActionOnCard.DELETE_CARD),
      });
      arFields.unshift(ActionOnCard.DELETE_CARD);
    }

    if (
      this._allowCardGenericOperation &&
      selectedCard.state === CardState.NOT_REGISTERED &&
      selectedCard.cardKind === CardKind.REAL_CARD &&
      this._cardService.hasAtLeastOneEnvironments()
    ) {
      actionSheetConfiguration.buttons.unshift({
        //icon: "pencil-outline",
        role: "",
        handler: () => actionSheet.dismiss(ActionOnCard.SET_UID),
      });
      arFields.unshift(ActionOnCard.SET_UID);
    }

    /*  Insert "Clone" button*/
    if (showClone && this._allowCardGenericOperation) {
      actionSheetConfiguration.buttons.unshift({
        //icon: "copy-outline",
        role: "",
        handler: () => actionSheet.dismiss(ActionOnCard.CLONE_CARD),
      });
      arFields.unshift(ActionOnCard.CLONE_CARD);
    }

    /*  Insert "Lock" button*/
    if (
      selectedCard.state === CardState.REGISTERED &&
      this._allowCardGenericOperation
    ) {
      actionSheetConfiguration.buttons.unshift({
        //icon: "lock-closed-outline",
        role: "",
        handler: () => actionSheet.dismiss(ActionOnCard.LOCK_CARD),
      });
      arFields.unshift(ActionOnCard.LOCK_CARD);
    }

    /* Insert "Activate/Unlock" button */
    if (
      (selectedCard.active &&
        selectedCard.state !== CardState.NOT_REGISTERED &&
        this._allowCardGenericOperation &&
        effectiveState !== CardEffectiveState.EXPIRED) ||
      (selectedCard.manufacturerSystem === ManufacturerSystem.SALTO_KS &&
        effectiveState !== CardEffectiveState.EXPIRED)
    ) {
      actionSheetConfiguration.buttons.unshift({
        //icon: "checkmark-circle-outline",
        role: "",
        handler: () => actionSheet.dismiss(ActionOnCard.ACTIVATE_UNLOCK_CARD),
      });
      arFields.unshift(ActionOnCard.ACTIVATE_UNLOCK_CARD);
    }

    /* Insert "Write card" button */
    if (showWriteCard && this._allowCardGenericOperation) {
      actionSheetConfiguration.buttons.unshift({
        //icon: "code-working-outline",
        role: "",
        handler: () => actionSheet.dismiss(ActionOnCard.WRITE_CARD),
      });
      arFields.unshift(ActionOnCard.WRITE_CARD);
    }

    /* Insert "Notify personal/client" button */
    let lblNotifyTo = "";
    if (
      (showNotifyCustomer || showNotifyStaff) &&
      this._allowCardGenericOperation
    ) {
      if (showNotifyCustomer) {
        lblNotifyTo = this._lblNotifyToCustomer;
      }
      if (showNotifyStaff) {
        lblNotifyTo = this._lblNotifyToStaff;
      }
      actionSheetConfiguration.buttons.unshift({
        //icon: "notifications-outline",
        role: "",
        handler: () => actionSheet.dismiss(ActionOnCard.NOTIFY_CARD),
      });
      arFields.unshift(ActionOnCard.NOTIFY_CARD);
    }

    /* Insert "Details" button */
    if (showCardEditSetting) {
      actionSheetConfiguration.buttons.unshift({
        //icon: "settings-outline",
        role: "",
        handler: () => actionSheet.dismiss(ActionOnCard.SHOW_CARD_SETTINGS),
      });
      arFields.unshift(ActionOnCard.SHOW_CARD_SETTINGS);
    }

    //#endregion

    actionSheet = await this._actionSheetController.create(
      actionSheetConfiguration
    );
    let title: string = "";
    let subTitle: string = "";
    for (let i = 0; i < arFields.length; i++) {
      let iconActionSheet = "";
      if (arFields[i] == ActionOnCard.ACTIVATE_UNLOCK_CARD) {
        iconActionSheet = "checkmark-circle-outline";
      }
      if (arFields[i] == ActionOnCard.SHOW_CARD_SETTINGS) {
        iconActionSheet = "settings-outline";
      }
      if (arFields[i] == ActionOnCard.NOTIFY_CARD) {
        iconActionSheet = "notifications-outline";
      }
      if (arFields[i] == ActionOnCard.SHOW_CARD_SETTINGS) {
        iconActionSheet = "settings-outline";
      }
      if (arFields[i] == ActionOnCard.LOCK_CARD) {
        iconActionSheet = "lock-closed-outline";
      }
      if (arFields[i] == ActionOnCard.CLONE_CARD) {
        iconActionSheet = "copy-outline";
      }
      if (arFields[i] == ActionOnCard.SET_UID) {
        iconActionSheet = "pencil-outline";
      }
      if (arFields[i] == ActionOnCard.WRITE_CARD) {
        iconActionSheet = "code-working-outline";
      }
      if (arFields[i] == ActionOnCard.DELETE_CARD) {
        iconActionSheet = "trash-outline";
      }

      const label = document.createElement("ion-label");
      label.setAttribute("style", "width:100%");

      label.innerHTML =
        "<table><tr><td rowspan='2' class='column-icon-action-sheet' ><ion-icon name='" +
        iconActionSheet +
        "'  class='icon-action-sheet'></ion-icon></td><td class='action-sheet-align'><div class='title-action-sheet' >" +
        this.getTitleActionSheet(arFields[i], lblNotifyTo) +
        "</div></td></tr><tr><td class='action-sheet-align'><div class='subtitle-action-sheet' >" +
        this.getSubtitleActionSheet(arFields[i]) +
        "</div></table>";

      const buttonEl = actionSheet.querySelectorAll(
        ".action-sheet-button-inner"
      );
      buttonEl[i].appendChild(label);
    }

    actionSheet.present();

    return await actionSheet.onDidDismiss();
  }

  /**
   * Open action sheet to create bills or quotes
   */
  public async openActionSheetBillsQuotesCreation(): Promise<any> {
    let actionSheet: any = null;
    const actionSheetConfiguration = {
      header: this._lblSelectNewDocument,
      cssClass: "left-align-buttons",
      buttons: [
        {
          text: this._lblBill,
          icon: "document-text-outline",
          handler: () => {
            actionSheet.dismiss(ActionInsertBillQuote.CREATE_NEW_BILL);
          },
        },
        {
          text: this._lblQuote,
          icon: "document-text-outline",
          handler: () => {
            actionSheet.dismiss(ActionInsertBillQuote.CREATE_NEW_QUOTE);
          },
        },
      ],
    };

    actionSheet = await this._actionSheetController.create(
      actionSheetConfiguration
    );

    actionSheet.present();

    return await actionSheet.onDidDismiss();
  }

  /**
   * Handle showing action sheet insert customer kind
   *
   */
  public async openActionSheetCustomerStaffGroupCreation(): Promise<any> {
    let actionSheet: any = null;
    const actionSheetConfiguration = {
      header: this._lblInsertNew,
      cssClass: "left-align-buttons",
      buttons: [
        {
          text: this._lblCustomer,
          icon: "person",
          handler: () => {
            actionSheet.dismiss(ActionInsertCustomer.CREATE_NEW_CUSTOMER);
          },
        },
        {
          text: this._lblStaff,
          icon: "build",
          handler: () => {
            actionSheet.dismiss(ActionInsertCustomer.CREATE_NEW_STAFF);
          },
        },
        {
          text: this._lblGroup,
          icon: "people",
          handler: () => {
            actionSheet.dismiss(ActionInsertCustomer.CREATE_NEW_GROUP);
          },
        },
        {
          text: this._lblClose,
          icon: "close",
          role: "cancel",
        },
      ],
    };

    actionSheet = await this._actionSheetController.create(
      actionSheetConfiguration
    );

    actionSheet.present();

    return await actionSheet.onDidDismiss();
  }

  /**
   * Handle select plate for specific customer staff
   */
  public async openActionSheetSelectPlate(
    currentStaff: CustomerStaff
  ): Promise<any> {
    const listCard = await this._cardService.getCardsByIDCustomerStaff(
      currentStaff.id,
      true
    );
    let actionSheet: any = null;
    let lblP1,
      lblP2,
      lblP3,
      lblP4,
      cssP1,
      cssP2,
      cssP3,
      cssP4: string = "";
    lblP1 = this._lblSetPlate1;
    lblP2 = this._lblSetPlate2;
    lblP3 = this._lblSetPlate3;
    lblP4 = this._lblSetPlate4;
    let returnValPlate1: number = 1;
    let returnValPlate2: number = 2;
    let returnValPlate3: number = 3;
    let returnValPlate4: number = 4;

    if (currentStaff.plate1) {
      cssP1 = "label-bold";
      lblP1 = currentStaff.plate1;
      if (this.searchPlateInList(listCard, currentStaff.plate1)) {
        cssP1 = " disable-action-sheet-btns";
        returnValPlate1 = -1;
      }
    }
    if (currentStaff.plate2) {
      cssP2 = "label-bold";
      lblP2 = currentStaff.plate2;
      if (this.searchPlateInList(listCard, currentStaff.plate2)) {
        cssP2 = " disable-action-sheet-btns";
        returnValPlate2 = -1;
      }
    }
    if (currentStaff.plate3) {
      lblP3 = currentStaff.plate3;
      cssP3 = "label-bold";
      if (this.searchPlateInList(listCard, currentStaff.plate3)) {
        cssP3 = " disable-action-sheet-btns";
        returnValPlate3 = -1;
      }
    }
    if (currentStaff.plate4) {
      lblP4 = currentStaff.plate4;
      cssP4 = "label-bold";
      if (this.searchPlateInList(listCard, currentStaff.plate4)) {
        cssP4 = " disable-action-sheet-btns";
        returnValPlate4 = -1;
      }
    }

    let actionSheetConfiguration = {
      header: this._lblSelectPlate,
      cssClass: "action-sheet-card-creation",
      buttons: [
        {
          text: this._lblClose,
          icon: "close",
          role: "cancel",
          data: {
            action: -1,
          },
        },
        {
          text: lblP1,
          icon: "add",
          cssClass:
            cssP1 +
            " icon-" +
            this._cardService.getCardKindIconClass(CardKind.PLATE),
          data: {
            action: returnValPlate1,
          },
        },
        {
          text: lblP2,
          icon: "add",
          cssClass:
            cssP2 +
            " icon-" +
            this._cardService.getCardKindIconClass(CardKind.PLATE),
          data: {
            action: returnValPlate2,
          },
        },
        {
          text: lblP3,
          icon: "add",
          cssClass:
            cssP3 +
            " icon-" +
            this._cardService.getCardKindIconClass(CardKind.PLATE),
          data: {
            action: returnValPlate3,
          },
        },
        {
          text: lblP4,
          icon: "add",
          cssClass:
            cssP4 +
            " icon-" +
            this._cardService.getCardKindIconClass(CardKind.PLATE),
          data: {
            action: returnValPlate4,
          },
        },
      ],
    };

    actionSheet = await this._actionSheetController.create(
      actionSheetConfiguration
    );

    actionSheet.present();
    this.replaceActionSheetPlateIcon();
    let result = await actionSheet.onDidDismiss();
    return result?.data?.action;
  }

  /**
   * Handle showing action sheet insert card
   *
   * @param listCardKindAvaiable List avaiable card kind
   * @returns Result operation
   */
  public async openActionSheetCardCreation(
    listCardKindAvaiable: Array<CardKind>
  ): Promise<any> {
    let actionSheet: any = null;
    const listButtonCardKind: Array<any> =
      this.composeButtonActionSheetCreation(actionSheet, listCardKindAvaiable);

    let actionSheetConfiguration = {
      header: this._lblSelectCardType,
      cssClass: "action-sheet-card-creation",
      buttons: [
        {
          text: this._lblClose,
          icon: "close",
          role: "cancel",
          data: {
            action: -1,
          },
        },
        ...listButtonCardKind,
      ],
    };

    actionSheet = await this._actionSheetController.create(
      actionSheetConfiguration
    );

    actionSheet.present();

    this.replaceActionSheetCardCreationIcon();
    let result = await actionSheet.onDidDismiss();
    return result?.data?.action;
  }
  //

  /**
   * Open action sheet to upload generic file from file chooser or to get camera
   *
   * @param allowCameraEntry Allow to get file from device videocamera
   * @param allowedDeviceNativeExtension List file extension (as string array) to get from file chooser for iOS and Android
   * @param allowedBrowserExtension Allow to get file from device videocamera (as string array) to get from file chooser for browserr
   * @returns
   */
  public async openActionSheetSelectUpload(
    allowCameraEntry: boolean = false
  ): Promise<any> {
    let actionSheet: any = null;
    const actionSheetConfiguration = {
      header: this._lblSelectUploadstrategy,
      buttons: [
        {
          text: this._lblClose,
          icon: "close",
          role: "cancel",
        },
      ],
    };

    if (!this._isMyTODevice) {
      const objectFileSelector: any = {
        text: this._lblSelectFromGallery,
        icon: "folder",
        /*
        handler: () => {
          actionSheet.dismiss(ActionUploadFile.FROM_FILE_CHOOSER);
        },
*/
        handler: () => {
          const fileChooserElement =
            document.getElementById("input-file-chooser");

          if (fileChooserElement) {
            fileChooserElement.click();
          }

          actionSheet.dismiss(ActionUploadFile.FROM_FILE_CHOOSER);
        },
      };

      actionSheetConfiguration.buttons.unshift(objectFileSelector);
    }
    //

    if (this._isMobileDevice && !this._isQ5Model && allowCameraEntry) {
      const objectOpenCamera: any = {
        text: this._lblTakeAPicture,
        icon: "camera",
        handler: async () => {
          actionSheet.dismiss(ActionUploadFile.FROM_CAMERA);
        },
      };

      actionSheetConfiguration.buttons.unshift(objectOpenCamera);
    }

    if (actionSheetConfiguration.buttons.length > 1) {
      actionSheet = await this._actionSheetController.create(
        actionSheetConfiguration
      );

      actionSheet.present();

      return await actionSheet.onDidDismiss();
    } else {
      return { data: null, role: null };
    }
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.insertModePlant",
        "dictionary.uploadQrCode",
        "dictionary.insertManualPlant",
        "dictionary.close",
        "dictionary.scannerQrCode",
        "dictionary.takeAPicture",
        "dictionary.selectFromGallery",
        "dictionary.directConnect",
        "dictionary.selectCardAction",
        "dictionary.delete",
        "dictionary.activateUnock",
        "dictionary.lock",
        "dictionary.sendNotificationToCustomerStaff",
        "dictionary.writeCardwithDevice",
        "dictionary.realCard",
        "dictionary.qrCode",
        "dictionary.numericCode",
        "dictionary.email",
        "dictionary.telephoneNumber",
        "dictionary.plate",
        "dictionary.faceRecognize",
        "dictionary.selectCardType",
        "dictionary.selectUploadStrategy",
        "dictionary.cardDetails",
        "dictionary.clone",
        "dictionary.notifyToCustomer",
        "dictionary.customer",
        "dictionary.staff",
        "dictionary.group",
        "dictionary.insertNew",
        "dictionary.selectPlate",
        "dictionary.setPlate1",
        "dictionary.setPlate2",
        "dictionary.setPlate3",
        "dictionary.setPlate4",
        "dictionary.setUID",
        "dictionary.notifyToStaff",
        "dictionary.descriptionWriteWithDevice",
        "dictionary.descriptionNotifyCard",
        "dictionary.descriptionClone",
        "dictionary.descriptionLock",
        "dictionary.descriptionDelete",
        "dictionary.descriptionSetUID",
        "dictionary.descriptionUnLock",
        "dictionary.descriptionCardDetail",
        "dictionary.viewAndModify",
        "dictionary.loadConfigurationFile",
        "dictionary.gatewayTypeActionTitle",
        "dictionary.selectDeviceType",
        "dictionary.manage",
        "dictionary.deviceManagement",
        "dictionary.associateExistingDevice",
        "dictionary.removeDeviceFromEnvironment",
        "dictionary.edit",
        "dictionary.viewConfiguration",
        "dictionary.loadExcel",
        "dictionary.bill",
        "dictionary.quote",
        "dictionary.selectNewDocument",
        "dictionary.addRoom",
        "dictionary.accessManagement",
        "dictionary.addPart",
        "dictionary.startDiscovery",
        "dictionary.importReadings"
      ])
      .subscribe((translation) => {
        this._lblInsertModePlant = translation["dictionary.insertModePlant"];
        this._lblUploadQrCode = translation["dictionary.uploadQrCode"];
        this._lblSelectNewDocument =
          translation["dictionary.selectNewDocument"];
        this._lblInsertManualPlant =
          translation["dictionary.insertManualPlant"];
        this._lblClose = translation["dictionary.close"];
        this._lblScannerQrCode = translation["dictionary.scannerQrCode"];
        this._lblSelectFromGallery =
          translation["dictionary.selectFromGallery"];
        this._lblTakeAPicture = translation["dictionary.takeAPicture"];
        this._lblDirectConnect = translation["dictionary.directConnect"];
        this._lblSelectCardOperation =
          translation["dictionary.selectCardAction"];
        this._lblDelete = translation["dictionary.delete"];
        this._lblActivateUnlock = translation["dictionary.activateUnock"];
        this._lblLock = translation["dictionary.lock"];
        this._lblNotify =
          translation["dictionary.sendNotificationToCustomerStaff"];
        this._lblWriteWithDevice =
          translation["dictionary.writeCardwithDevice"];
        this._lblRealCard = translation["dictionary.realCard"];
        this._lblQrCode = translation["dictionary.qrCode"];
        this._lblNumericCode = translation["dictionary.numericCode"];
        this._lblMail = translation["dictionary.email"];
        this._lblTelephoneNumber = translation["dictionary.telephoneNumber"];
        this._lblPlate = translation["dictionary.plate"];
        this._lblFaceRecognize = translation["dictionary.faceRecognize"];
        this._lblSelectCardType = translation["dictionary.selectCardType"];
        this._lblSelectUploadstrategy =
          translation["dictionary.selectUploadStrategy"];
        this._lblCardDetail = translation["dictionary.cardDetails"];
        this._lblClone = translation["dictionary.clone"];
        this._lblNotifyToCustomer = translation["dictionary.notifyToCustomer"];
        this._lblNotifyToStaff = translation["dictionary.notifyToStaff"];
        this._lblSetUID = translation["dictionary.setUID"];

        this._lblCustomer = translation["dictionary.customer"];
        this._lblBill = translation["dictionary.bill"];
        this._lblQuote = translation["dictionary.quote"];
        this._lblStaff = translation["dictionary.staff"];
        this._lblGroup = translation["dictionary.group"];
        this._lblInsertNew = translation["dictionary.insertNew"];
        this._lblSelectPlate = translation["dictionary.selectPlate"];
        this._lblSetPlate1 = translation["dictionary.setPlate1"];
        this._lblSetPlate2 = translation["dictionary.setPlate2"];
        this._lblSetPlate3 = translation["dictionary.setPlate3"];
        this._lblSetPlate4 = translation["dictionary.setPlate4"];

        this._lblDescriptionWriteWithDevice =
          translation["dictionary.descriptionWriteWithDevice"];
        this._lblDescriptionNotifyCard =
          translation["dictionary.descriptionNotifyCard"];
        this._lblDescriptionClone = translation["dictionary.descriptionClone"];
        this._lblDescriptionLock = translation["dictionary.descriptionLock"];
        this._lblDescriptionDelete =
          translation["dictionary.descriptionDelete"];
        this._lblDescriptionSetUID =
          translation["dictionary.descriptionSetUID"];
        this._lblDescriptionActivateUnlock =
          translation["dictionary.descriptionUnLock"];
        this._lblDescriptionCardDetail =
          translation["dictionary.descriptionCardDetail"];
        this._lblViewAndModify = translation["dictionary.viewAndModify"];
        this._lblLoadConfigurationFile =
          translation["dictionary.loadConfigurationFile"];
        this._lblGatewayActionTitle =
          translation["dictionary.gatewayTypeActionTitle"];
        this._lblSelectDeviceType = translation["dictionary.selectDeviceType"];
        this._lblManage = translation["dictionary.manage"];
        this._lblDeviceManagement = translation["dictionary.deviceManagement"];
        this._lblAssociateExistingDevice =
          translation["dictionary.associateExistingDevice"];
        this._lblRemoveDeviceFromEnvironment =
          translation["dictionary.removeDeviceFromEnvironment"];
        this._lblEdit = translation["dictionary.edit"];
        this._viewConfiguration = translation["dictionary.viewConfiguration"];
        this._loadExcel = translation["dictionary.loadExcel"];
        this._lblAddRoom = translation["dictionary.addRoom"];
        this._lblAccessManagement = translation["dictionary.accessManagement"];
        this._lblAddPart = translation["dictionary.addPart"];
        this._lblStartDiscovery = translation["dictionary.startDiscovery"];
        this._lblImportReadings = translation["dictionary.importReadings"]
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion
  /**
   * Handle open action sheet  gateway types
   */
  public async openActionSheetGatewayTypes(): Promise<any> {
    const actionSheetGatewayTypesCtrl =
      await this._actionSheetController.create({
        header: this._lblGatewayActionTitle,
        buttons: [
          {
            text: "Connect Box",
            icon: "cog-outline",
            data: {
              action: ActionGatewayType.CONNECT_BOX,
              gatewayId: 1,
            },
          },
          {
            text: "Logic Machine",
            icon: "cog-outline",
            data: {
              action: ActionGatewayType.LOGIC_MACHINE,
              gatewayId: 2,
            },
          },
          {
            text: "ALFA - Sinapsi",
            icon: "cog-outline",
            data: {
              action: ActionGatewayType.ALFA_SINAPSI,
              gatewayId: 3,
            },
          },
          {
            text: "Equobox- Sinapsi",
            icon: "cog-outline",
            data: {
              action: ActionGatewayType.ALFA_SINAPSI,
              gatewayId: 4,
            },
          },
          {
            text: this._lblClose,
            icon: "close",
            role: "cancel",
          },
        ],
      });
    actionSheetGatewayTypesCtrl.present();
    const actionSheetResult = await actionSheetGatewayTypesCtrl.onDidDismiss();
    return actionSheetResult;
  }
  /**
   * Handles the opening of actionsheet confifuration on the list gateway page
   */
  public async openActionSheetListGatewayConfiguration(): Promise<any> {
    const actionSheetListGateway = await this._actionSheetController.create({
      buttons: [
        {
          text: this._lblViewAndModify,
          icon: "create-outline",
          data: {
            action: ActionListGateway.VIEW_AND_MODIFY,
          },
        },
        {
          text: this._lblLoadConfigurationFile,
          icon: "document-outline",
          data: {
            action: ActionListGateway.LOAD_CONFIGURATION_FILE,
          },
        },
        {
          text: this._lblClose,
          icon: "close",
          role: "cancel",
        },
      ],
    });
    actionSheetListGateway.present();
    const dismissedData = await actionSheetListGateway.onDidDismiss();
    return dismissedData;
  }

  /**
   * Choose the type of device to add: grid of tiles (same popup as the building part types).
   *
   * @returns `{ data: { action } }` with the chosen {@link ActionDeviceType}, as the former
   * action sheet did (`data` is undefined when the popup is closed)
   */
  public async openActionSheetDeviceManagement(): Promise<any> {
    const deviceTypes = [
      { id: ActionDeviceType.ASSOCIATE_EXISTING_DEVICE, text: this._lblAssociateExistingDevice, icon: "link-outline" },
      { id: ActionDeviceType.SINAPSI_ALFA, text: "Sinapsi - ALFA", icon: "git-network-outline" },
      { id: ActionDeviceType.SINAPSI_RTUEVO, text: "Sinapsi - RTUEVO", icon: "git-network-outline" },
      { id: ActionDeviceType.SMARTGATEWAY, text: "Smartgateway", icon: "git-network-outline" },
      { id: ActionDeviceType.CONNECT_BOX, text: "Connect Box", icon: "cube-outline" },
      { id: ActionDeviceType.GATEWAY_IOT, text: "Gateway IoT", icon: "wifi-outline" },
      // { id: ActionDeviceType.KNX, text: "KNX", icon: "cog-outline" },
    ];

    const modal = await this._modalController.create({
      component: PopupEnvironmentCategoryComponent,
      cssClass: "category-picker-modal",
      componentProps: {
        header: this._lblSelectDeviceType,
        categories: deviceTypes,
      },
    });
    await modal.present();

    const result = await modal.onDidDismiss();
    return { data: result.data ? { action: result.data } : undefined };
  }

  /**
   * Open the actions menu of a building part, as a popover anchored to the button pressed.
   * Same entries and conditions as before (the old action sheet's "Close" is not needed:
   * tapping outside closes the popover); "Delete" is shown last, in red.
   *
   * @param event click event of the ⋮ button (popover anchor)
   * @returns dismiss result; `data.action` is the chosen {@link ActionEnvironments}
   */
  public async openMenuActionEnvironment(
    event: Event,
    category: EnvironmentCategory,
    settings: Settings,
    isDemoMode: boolean = false,
    envHasDevicesAndChildren: boolean,
    removable: boolean = true
  ): Promise<any> {
    const canChange = settings.AllowChangesBuildingPart && !isDemoMode;
    const actions: Array<ActionMenuItem<ActionEnvironments>> = [];

    if (settings.ShowDeviceListPartsBuilding) {
      actions.push({
        icon: "cog-outline",
        text: this._lblDeviceManagement,
        action: ActionEnvironments.MANAGE_DEVICES,
      });
    }
    if (settings.ShowSetup_AccessControl) {
      actions.push({
        icon: "key-outline",
        text: this._lblAccessManagement,
        action: ActionEnvironments.ACCESS_MANAGEMENT,
      });
    }
    if (canChange) {
      actions.push({
        icon: "create-outline",
        text: this._lblManage,
        action: ActionEnvironments.UPDATE,
      });
    }
    if (
      canChange &&
      category !== EnvironmentCategory.ROOM &&
      category !== EnvironmentCategory.COMMON_AREA
    ) {
      actions.push({
        icon: "add-circle-outline",
        text: this._lblAddPart,
        action: ActionEnvironments.ADD_PART,
      });
    }
    if (
      canChange &&
      category === EnvironmentCategory.CONDOMINIUM &&
      !this._isMobileDevice
    ) {
      actions.push({
        icon: "analytics-outline",
        text: this._lblImportReadings,
        action: ActionEnvironments.IMPORT_READINGS,
      });
    }
    if (canChange && !envHasDevicesAndChildren && removable) {
      actions.push({
        icon: "trash-outline",
        text: this._lblDelete,
        action: ActionEnvironments.DELETE,
        danger: true,
      });
    }

    const popover = await this._popoverController.create({
      component: PopoverActionMenuComponent,
      componentProps: { actions },
      event,
      side: "bottom",
      alignment: "end",
      cssClass: "action-menu-popover",
    });

    await popover.present();
    return await popover.onDidDismiss();
  }

  /**
   * Open the actions menu of a Sinapsi device, as a popover anchored to the button pressed.
   * Same entries and conditions as the former action sheet; "Remove from the part" last, in red.
   *
   * @param event click event of the ⋮ button (popover anchor)
   * @param isKnownDevice id known sinapsi device
   * @param deviceOrderNumberOne sinapsi device order number
   * @param isConnectBoxDevice connect box device
   * @returns dismiss result; `data.action` is the chosen {@link ActionSinapsiDevice}
   */
  public async openMenuSinapsiDevice(
    event: Event,
    isKnownDevice: boolean,
    deviceOrderNumberOne: string,
    isConnectBoxDevice: boolean
  ): Promise<any> {
    const actions: Array<ActionMenuItem<ActionSinapsiDevice>> =
      this.createSInapsiDevicesModificationButtonForActionSheet(
        isKnownDevice,
        deviceOrderNumberOne,
        isConnectBoxDevice
      ).map((button) => ({
        icon: button.icon,
        text: button.text,
        action: button.data?.action,
      }));

    actions.push({
      icon: "trash-outline",
      text: this._lblRemoveDeviceFromEnvironment,
      action: ActionSinapsiDevice.DELETE,
      danger: true,
    });

    const popover = await this._popoverController.create({
      component: PopoverActionMenuComponent,
      componentProps: { actions },
      event,
      side: "bottom",
      alignment: "end",
      cssClass: "action-menu-popover",
    });

    await popover.present();
    return await popover.onDidDismiss();
  }

  /**
   * Compose action sheet button by avaiable list card kind
   *
   * @param actionSheet  Current action sheet
   * @param listCardKind List card kind
   * @returns
   */
  private composeButtonActionSheetCreation(
    actionSheet: any,
    listCardKind: Array<CardKind>
  ): Array<any> {
    let listButton: Array<any> = new Array<any>();

    for (let cardKind of listCardKind) {
      switch (cardKind) {
        case CardKind.REAL_CARD:
          listButton.push({
            text: this._lblRealCard + "",
            cssClass:
              "icon-" +
              this._cardService.getCardKindIconClass(CardKind.REAL_CARD),
            icon: "add",
            data: {
              action: CardKind.REAL_CARD,
            },
          });
          break;
        case CardKind.QRCODE:
          listButton.push({
            text: this._lblQrCode,
            icon: "add",
            cssClass:
              "icon-" + this._cardService.getCardKindIconClass(CardKind.QRCODE),
            data: {
              action: CardKind.QRCODE,
            },
          });
          break;
        case CardKind.VIRTUAL_KEYPAD_CODE:
          listButton.push({
            text: this._lblNumericCode,
            icon: "add",
            cssClass:
              "icon-" +
              this._cardService.getCardKindIconClass(
                CardKind.VIRTUAL_KEYPAD_CODE
              ),
            data: {
              action: CardKind.VIRTUAL_KEYPAD_CODE,
            },
          });
          break;
        case CardKind.MAIL:
          listButton.push({
            text: this._lblMail,
            icon: "add",
            cssClass:
              "icon-" + this._cardService.getCardKindIconClass(CardKind.MAIL),
            data: {
              action: CardKind.MAIL,
            },
          });
          break;
        case CardKind.PHONE_NUMBER:
          listButton.push({
            text: this._lblTelephoneNumber,
            icon: "add",
            cssClass:
              "icon-" +
              this._cardService.getCardKindIconClass(CardKind.PHONE_NUMBER),
            data: {
              action: CardKind.PHONE_NUMBER,
            },
          });
          break;
        case CardKind.PLATE:
          listButton.push({
            text: this._lblPlate,
            icon: "add",
            cssClass:
              "icon-" + this._cardService.getCardKindIconClass(CardKind.PLATE),
            data: {
              action: CardKind.PLATE,
            },
          });
          break;
        case CardKind.FACE:
          listButton.push({
            text: this._lblFaceRecognize,
            icon: "add",
            cssClass:
              "icon-" + this._cardService.getCardKindIconClass(CardKind.FACE),
            data: {
              action: CardKind.FACE,
            },
          });
          break;
      }
    }

    return listButton;
  }

  /**
   * Check if current platform is mobile
   */
  private checkMobileCondition(): void {
    this._isMobileDevice =
      this._platform.is("ios") || this._platform.is("android");

    this._isMyTODevice = this._bigService.isMyToDevice;
    this._isQ5Model = this._bigService.isQ5Model;
    this._isQ896SModel = this._bigService.isQ896SModel;
  }

  /**
   * Workaround for easy replace icon in action sheet ionic component:
   * by default it's hard to replace default icon set referenced to ionic action sheet button.
   * For replace these icon, it need to assign a specific class item, then adding font class to button
   */
  private replaceActionSheetInsertPlantModeIcon(): void {
    document
      .querySelector(".icon-bigstudio-insert-manual ion-icon")
      .classList.add("icon-bigstudio3");
    document
      .querySelector(".icon-bigstudio-insert-manual ion-icon")
      .classList.add("bigstudio3-codepen");

    document
      .querySelector(".icon-bigstudio-upload ion-icon")
      .classList.add("icon-bigstudio3");
    document
      .querySelector(".icon-bigstudio-upload ion-icon")
      .classList.add("bigstudio3-qrcode");

    if (document.querySelector(".icon-bigstudio-scan-qr ion-icon")) {
      document
        .querySelector(".icon-bigstudio-scan-qr ion-icon")
        .classList.add("icon-bigstudio3");
      document
        .querySelector(".icon-bigstudio-scan-qr ion-icon")
        .classList.add("bigstudio3-camera");
    }
  }

  /**
   * Replace action sheet plate icon
   */
  private replaceActionSheetPlateIcon(): void {
    const listCardKind: Array<any> = Object.values(CardKind).filter(
      (cardKind: any) => !isNaN(cardKind.toString())
    );

    for (let cKind in listCardKind) {
      let cardKindClass: string = this._cardService.getCardKindIconClass(
        Number(cKind)
      );
      let cssClassButton: string = "icon-" + cardKindClass;
      const buttonElement = document.querySelectorAll(
        "." + cssClassButton + " ion-icon"
      );

      buttonElement.forEach(function (value) {
        value.classList.add("icon-bigstudio3", cardKindClass);
      });
    }
  }

  /**
   * Replace action sheet create card icon
   */
  private replaceActionSheetCardCreationIcon(): void {
    const listCardKind: Array<any> = Object.values(CardKind).filter(
      (cardKind: any) => !isNaN(cardKind.toString())
    );

    for (let cKind in listCardKind) {
      let cardKindClass: string = this._cardService.getCardKindIconClass(
        Number(cKind)
      );
      let cssClassButton: string = "icon-" + cardKindClass;
      const buttonElement = document.querySelector(
        "." + cssClassButton + " ion-icon"
      );

      if (buttonElement) {
        buttonElement.classList.add("icon-bigstudio3", cardKindClass);
      }
    }
  }

  /**
   * Load server settings from setting service
   */
  private loadSettings(): void {
    this._selectServerSettingsUserSub = this._store
      .select(serverSettings)
      .pipe(filter((setting) => setting !== null))
      .subscribe((setting: Settings) => {
        this._allowCardEdit = setting.AllowCardEdit;
        this._allowCardEnvironmentEdit = setting.AllowCardEnvironmentEdit;
        this._allowCardGenericOperation = setting.AllowCardGenericOperation;
        this._allowCardDelete = setting.AllowCardDelete;
      });
  }

  /**
   * Search if plate is in listCard
   * @param listCard
   * @param plate
   * @returns
   */
  private searchPlateInList(listCard: Array<Card>, plate: string): Card {
    return listCard.find((obj) => {
      return (
        obj.cardCode == plate && obj.cardKind == CardKind.PLATE && obj.active
      );
    });
  }

  /**
   * Get the title of action sheet for card
   * @param typeAction
   * @param lblNotify
   * @returns
   */
  private getTitleActionSheet(typeAction: ActionOnCard, lblNotify: string) {
    let title: string = "";
    if (typeAction == ActionOnCard.SHOW_CARD_SETTINGS) {
      title = this._lblCardDetail;
    }
    if (typeAction == ActionOnCard.NOTIFY_CARD) {
      title = lblNotify;
    }
    if (typeAction == ActionOnCard.CLONE_CARD) {
      title = this._lblClone;
    }
    if (typeAction == ActionOnCard.WRITE_CARD) {
      title = this._lblWriteWithDevice;
    }
    if (typeAction == ActionOnCard.LOCK_CARD) {
      title = this._lblLock;
    }
    if (typeAction == ActionOnCard.DELETE_CARD) {
      title = this._lblDelete;
    }
    if (typeAction == ActionOnCard.SET_UID) {
      title = this._lblSetUID;
    }
    if (typeAction == ActionOnCard.ACTIVATE_UNLOCK_CARD) {
      title = this._lblActivateUnlock;
    }
    return title;
  }

  /**
   * Get the subtitle for action sheet for card
   * @param typeAction
   * @returns
   */
  private getSubtitleActionSheet(typeAction: ActionOnCard) {
    let subTitle: string = "";
    if (typeAction == ActionOnCard.SHOW_CARD_SETTINGS) {
      subTitle = this._lblDescriptionCardDetail;
    }
    if (typeAction == ActionOnCard.NOTIFY_CARD) {
      subTitle = this._lblDescriptionNotifyCard;
    }
    if (typeAction == ActionOnCard.CLONE_CARD) {
      subTitle = this._lblDescriptionClone;
    }
    if (typeAction == ActionOnCard.WRITE_CARD) {
      subTitle = this._lblDescriptionWriteWithDevice;
    }
    if (typeAction == ActionOnCard.LOCK_CARD) {
      subTitle = this._lblDescriptionLock;
    }
    if (typeAction == ActionOnCard.DELETE_CARD) {
      subTitle = this._lblDescriptionDelete;
    }
    if (typeAction == ActionOnCard.SET_UID) {
      subTitle = this._lblDescriptionSetUID;
    }
    if (typeAction == ActionOnCard.ACTIVATE_UNLOCK_CARD) {
      subTitle = this._lblDescriptionActivateUnlock;
    }
    return subTitle;
  }

  /**
   * Create the modification button if the Sinapsi device falls into any of the EquoboxMedium
   * @param isKnownSinapsiDevice
   * @param deviceOrderNumberOne
   * @returns
   */
  private createSInapsiDevicesModificationButtonForActionSheet(
    isKnownDevice: boolean,
    deviceOrderNumberOne: string,
    isConnectBoxDevice: boolean
  ): Array<any> {
    let modifyButton: Array<any> = [];
    if (isKnownDevice) {
      switch (deviceOrderNumberOne) {
        case EquoboxMedium.EQUOBOX_MEDIUM_OTHER:
        case EquoboxMedium.EQUOBOX_MEDIUM_COOLING_LOAD_METER_VOLUME_FLOW_TEMP_INLET:
        case EquoboxMedium.EQUOBOX_MEDIUM_COOLING_LOAD_METER_VOLUME_RETURN_TEMP_OUTLET:
        case EquoboxMedium.EQUOBOX_MEDIUM_ELECTRICITY:
        case EquoboxMedium.EQUOBOX_MEDIUM_GAS:
        case EquoboxMedium.EQUOBOX_MEDIUM_HEAT:
        case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_A_D_CONVERTER:
        case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_BUS_SYSTEM:
        case EquoboxMedium.EQUOBOX_MEDIUM_COLD_WATER:
        case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_COOLING_LOAD_METER:
        case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_COST_ALLOCATOR:
        case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_DUAL_WATER:
        case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_PRESSURE:
        case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_UNKNOWN_MEDIUM:
        case EquoboxMedium.EQUOBOX_MEDIUM_HEAT_VOLUME_FLOW_TEMP_INLET:
        case EquoboxMedium.EQUOBOX_MEDIUM_HOT_WATER:
        case EquoboxMedium.EQUOBOX_MEDIUM_OIL:
        case EquoboxMedium.EQUOBOX_MEDIUM_STEAM:
        case EquoboxMedium.EQUOBOX_MEDIUM_WATER:
          modifyButton.push({
            text: this._lblEdit,
            icon: "create-outline",
            data: {
              action: ActionSinapsiDevice.MODIFY,
            },
          });
          break;
        case "SIN.EQRTUEVO2T":
          modifyButton.push({
            text: this._lblStartDiscovery,
            icon: "play-outline",
            data: {
              action: ActionSinapsiDevice.START_DISCOVERY,
            },
          })
          break;
      }
    }
    if (isConnectBoxDevice) {
      modifyButton.push(
        {
          text: this._viewConfiguration,
          icon: "eye-outline",
          data: {
            action: ActionSinapsiDevice.VIEW_CONFIGURATION,
          },
        },
        {
          text: this._loadExcel,
          icon: "document-outline",
          data: {
            action: ActionSinapsiDevice.LOAD_EXCEL,
          },
        }
      );
    }
    return modifyButton;
  }
}
